import clsx from "clsx";
import React from "react";

import { ITeachingMaterial, ILesson } from "../../state";
import ModalConfirm from "../../molecules/ModalConfirm";
import LessonBrief from "../../molecules/LessonBrief";
import ModalPreparationConfirm from "../../molecules/ModalPreparationConfirm";

import styles from "./TeachingMaterial.module.scss";

export interface TeachingMaterialProps {
  teaching_material: ITeachingMaterial;
  calling: boolean;
  onPublishLesson: (lesson_id: number) => void;
  openGuideline: (teaching_material_id: number, lesson_id: number) => void;
  openMaterial: (teaching_material_id: number, lesson_id: number) => void;
}

type ScreenType = "old_guideline" | "guideline" | "material";

const TeachingMaterial = ({
  teaching_material,
  calling,
  onPublishLesson,
  openGuideline,
  openMaterial,
}: TeachingMaterialProps) => {
  const [confirmStatusLesson, setConfirmStatusLesson] = React.useState<{
    isShowModal: boolean;
    lessonId: number;
    screenType: ScreenType;
  } | null>();
  const [
    confirmLessonPreparationImage,
    setConfirmLessonPreparationImage,
  ] = React.useState<{
    isShowModal: boolean;
    lessonId: number;
    url: string;
  } | null>();

  const goToNextScreen = React.useCallback(
    (lessonId: number, screenType: ScreenType) => {
      switch (screenType) {
        case "guideline":
          openGuideline(teaching_material.id, lessonId);
          break;
        case "material":
          openMaterial(teaching_material.id, lessonId);
          break;
      }
    },
    [openGuideline, openMaterial, teaching_material.id],
  );

  const checkPreparationImage = (
    lesson: ILesson | undefined,
    screenType: ScreenType,
  ): boolean => {
    if (lesson && lesson.preparation_image_url && screenType === "guideline") {
      setConfirmLessonPreparationImage({
        isShowModal: true,
        lessonId: lesson.id,
        url: lesson.preparation_image_url,
      });
      return false;
    }
    return true;
  };

  const handleOnClick = React.useCallback(
    (lesson: ILesson, screenType: ScreenType) => {
      if (!lesson.published && !lesson.teacher_only) {
        setConfirmStatusLesson({
          isShowModal: true,
          lessonId: lesson.id,
          screenType,
        });
        return;
      }

      if (checkPreparationImage(lesson, screenType)) {
        goToNextScreen(lesson.id, screenType);
      }
    },
    [goToNextScreen],
  );

  const openPreparation = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <div className={styles.container}>
      <div
        className={clsx(styles.cover)}
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3),
        rgba(0, 0, 0, 0.3)), url("${teaching_material.image_url}")`,
        }}
        key={teaching_material.id}
        data-bs-toggle="collapse"
        data-bs-target={`#lesson${teaching_material.id.toString()}`}
        role="button"
        aria-expanded="false"
        aria-controls={`#lesson${teaching_material.id.toString()}`}
      >
        <div className={styles.header}>
          <div
            className={clsx(
              styles.name,
              teaching_material.kind?.startsWith("base") && styles.base,
            )}
          >
            {teaching_material.name}
          </div>
          <div className={styles.title}>{teaching_material.title}</div>
        </div>
      </div>
      {teaching_material.lessons && (
        <div
          className={`collapse ${styles.collapseContent}`}
          id={`lesson${teaching_material.id.toString()}`}
          data-bs-parent="#accordion"
        >
          {teaching_material.lessons.map(l => (
            <LessonBrief
              key={l.id}
              lesson={l}
              material_id={teaching_material.id}
              handleOpenGuideline={() => handleOnClick(l, "guideline")}
              handleOpenMaterial={() => handleOnClick(l, "material")}
            />
          ))}
        </div>
      )}
      {confirmStatusLesson && confirmStatusLesson.isShowModal && (
        <ModalConfirm
          onClose={() => {
            !calling && setConfirmStatusLesson(null);
          }}
          title={`このレッスンは生徒に未開放です。\n開放しますか？`}
          buttons={[
            {
              title: `開放せずに${
                confirmStatusLesson.screenType === "material"
                  ? "投影画面"
                  : "指導書"
              }を開く`,
              action: () => {
                if (!calling) {
                  setConfirmStatusLesson(null);
                  const lesson = teaching_material.lessons?.find(
                    lesson => lesson.id === confirmStatusLesson?.lessonId,
                  );
                  if (
                    checkPreparationImage(
                      lesson,
                      confirmStatusLesson.screenType,
                    )
                  ) {
                    goToNextScreen(
                      confirmStatusLesson.lessonId,
                      confirmStatusLesson.screenType,
                    );
                  }
                }
              },
              type: "None",
            },
            {
              title: "開放する",
              action: () => {
                if (!calling) {
                  onPublishLesson(confirmStatusLesson.lessonId);
                  setConfirmStatusLesson(null);
                }
              },
              type: "Danger",
            },
          ]}
        />
      )}
      {confirmLessonPreparationImage &&
        confirmLessonPreparationImage.isShowModal && (
          <ModalPreparationConfirm
            onClose={() => {
              !calling && setConfirmLessonPreparationImage(null);
            }}
            openGuideline={() => {
              if (!calling) {
                setConfirmLessonPreparationImage(null);
                goToNextScreen(
                  confirmLessonPreparationImage.lessonId,
                  "guideline",
                );
              }
            }}
            openPreparation={() => {
              if (!calling) {
                setConfirmLessonPreparationImage(null);
                openPreparation(confirmLessonPreparationImage.url);
              }
            }}
          />
        )}
    </div>
  );
};

export default TeachingMaterial;
